/**
 * Initiatives — the strategic layer above Projects that powers the delivery
 * roadmap. An initiative groups projects toward an outcome over a time window.
 * Status is a fixed lifecycle (below); progress is rolled up from the
 * initiative's projects' tasks at read time.
 */

export enum InitiativeStatus {
  PLANNED = 'planned',
  ACTIVE = 'active',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface InitiativeStatusMeta {
  id: InitiativeStatus
  label: string
  color: string
  /** Terminal state — excluded from "active" rollups and roadmap defaults. */
  isClosed: boolean
}

export const INITIATIVE_STATUSES: InitiativeStatusMeta[] = [
  { id: InitiativeStatus.PLANNED, label: 'Planned', color: '#6B7280', isClosed: false },
  { id: InitiativeStatus.ACTIVE, label: 'Active', color: '#3B82F6', isClosed: false },
  { id: InitiativeStatus.PAUSED, label: 'Paused', color: '#F59E0B', isClosed: false },
  { id: InitiativeStatus.COMPLETED, label: 'Completed', color: '#10B981', isClosed: true },
  { id: InitiativeStatus.CANCELLED, label: 'Cancelled', color: '#EF4444', isClosed: true },
]

export function getInitiativeStatusMeta(
  status: string,
): InitiativeStatusMeta | undefined {
  return INITIATIVE_STATUSES.find(s => s.id === status)
}

export interface Initiative {
  id: string
  organizationId: string
  name: string
  description: string | null
  status: string
  color: string | null
  icon: string | null
  ownerId: string | null
  startDate: Date | string | null
  targetDate: Date | string | null
  sortOrder: number
  createdBy: string | null
  createdAt: Date | string
  updatedAt: Date | string
}

/** A project summarized for an initiative's roadmap row. */
export interface InitiativeProjectSummary {
  id: string
  name: string
  status: string
  color: string | null
  startDate: Date | string | null
  endDate: Date | string | null
  taskCount: number
  completedTaskCount: number
  progress: number // 0-100
}

export interface InitiativeOwnerSummary {
  id: string
  firstName: string | null
  lastName: string | null
  displayName: string | null
  email: string
  image: string | null
}

/** Initiative enriched with rolled-up delivery progress for list/roadmap. */
export interface InitiativeWithProgress extends Initiative {
  owner: InitiativeOwnerSummary | null
  projectCount: number
  completedProjectCount: number
  taskCount: number
  completedTaskCount: number
  /** 0-100, averaged across the initiative's projects. */
  progress: number
  projects: InitiativeProjectSummary[]
  /**
   * Customer demand behind this initiative's delivery work, rolled up through
   * the chain: feature-request ticket → promoted task → project → initiative.
   */
  requestCount: number
  requesterCount: number
  requesterWeight: number | null
}

export interface CreateInitiative {
  name: string
  description?: string | null
  status?: string
  color?: string | null
  icon?: string | null
  ownerId?: string | null
  startDate?: Date | string | null
  targetDate?: Date | string | null
  sortOrder?: number
  projectIds?: string[]
}

export interface UpdateInitiative {
  name?: string
  description?: string | null
  status?: string
  color?: string | null
  icon?: string | null
  ownerId?: string | null
  startDate?: Date | string | null
  targetDate?: Date | string | null
  sortOrder?: number
}
